/*
 * (c) Copyright, 2023, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import _ from 'lodash'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '~/root'
import { createFolder, getDetails } from '~/services/api/files'
import Modal from '../Modal'
import { Button, Form } from 'react-bootstrap'
import { Formik } from 'formik'
import Field from '../shared/Field'
import { Select } from '../Select'
import { getAllFolderTypes } from '~/services/api/api.folderstructuretemplatetypes'
import { folderTypesService } from '~/services/api/folderTypes'
import { toast } from '~/services/toast'
import DocTypeDropdown from '../pickers/DocTypeDropdown'
import Utils from '~/lib/Utils'

interface AddFolderModalProps {
  parentFolderId: string | number
  isFolderStructure?: boolean
  modalLevelClasses?: { modalClass?: string }
}

export function isValid(value: string, isRestrictEmojis?: boolean): boolean {
  // Use ES6 regex if supported, else fallback to basic emoji detection
  let regexForEmojis: RegExp
  try {
    regexForEmojis = new RegExp('\\p{Extended_Pictographic}', 'u')
  } catch {
    // Fallback: match common emoji unicode range
    // Fallback: match common emoji characters (basic, not full unicode)
    regexForEmojis = /[\u263A-\u263C]/
  }
  // filename is . or .. (aka current/parent)
  if (value) {
    if (value === '.' || value === '..') {
      return false
    } else if (
      value.indexOf('/') > -1 ||
      value.indexOf('\\') > -1 ||
      value.indexOf(':') > -1 ||
      value.indexOf('*') > -1 ||
      value.indexOf('?') > -1 ||
      value.indexOf('"') > -1 ||
      value.indexOf('<') > -1 ||
      value.indexOf('>') > -1 ||
      value.indexOf('|') > -1 ||
      value.indexOf('\t') > -1 ||
      value.indexOf('\r') > -1 ||
      value.indexOf('\n') > -1 ||
      value.indexOf('\f') > -1 ||
      value.indexOf('\b') > -1
    ) {
      return false
    }
    // first or last character is a space
    else if (/^\s+|\s+$/.test(value)) {
      return false
    } else if (isRestrictEmojis && regexForEmojis.test(value)) {
      return false
    }
  }
  return true
}

const AddFolderModal = ({ parentFolderId, isFolderStructure, modalLevelClasses }: AddFolderModalProps) => {
  const context = useServiceContext()
  const { constant: Constants } = context
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(true)
  const formRef = useRef<any>(null)
  const [folderTypes, setFolderTypes] = useState<any[]>([])
  const [availableFolderStructureTemplateTypes, setAvailableFolderStructureTemplateTypes] = useState<any[]>([])
  const [request, setRequest] = useState<any>({ folderTypeTemplateId: null })
  const [selectedFolderType, setSelectedFolderType] = useState<any>(null)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [allowedDocTypeIds, setAllowedDocTypeIds] = useState<number[]>([])
  const [selectedDocTypes, setSelectedDocTypes] = useState<any[]>([])
  const defaultConfigData = {
    data: {
      folderName: '',
      folderType: null
    }
  }

  useEffect(() => {
    const requestData = { ...request }
    if (isFolderStructure) {
      getAllFolderTypes().then(function (data: any) {
        setAvailableFolderStructureTemplateTypes(data?.elements ?? [])
      })
    }

    folderTypesService.getAllFolderTypes().then(
      function (data: any) {
        const availableFolderTypes = data?.elements ?? []
        setFolderTypes(availableFolderTypes)
        if (!isFolderStructure) {
          getDetails(Number(parentFolderId)).then(
            function (data: any) {
              if (data?.fsiTypeTemplate) {
                const parentDocType = _.find(availableFolderTypes, { id: data?.fsiTypeTemplate?.id })
                setSelectedFolderType(parentDocType)
                requestData.folderTypeTemplateId = parentDocType
                if (
                  requestData?.folderTypeTemplateId?.documentTypesAllowed ===
                  Constants?.system?.settings?.folderType?.documentTypesAllowed?.DEFINED_ON_FOLDER
                ) {
                  const allowedIds = data?.allowedDocumentTypeTemplateIds ?? []
                  setAllowedDocTypeIds(allowedIds)
                  requestData.allowedDocumentTypeTemplateIds = allowedIds
                }
                setRequest(requestData)
              }
            },
            (error: any) => {
              toast.error(translate(error?.data?.message ?? ''))
            }
          )
        }
      },
      (error: any) => {
        toast.error(translate(error?.data?.message ?? ''))
      }
    )
  }, [parentFolderId])

  const save = () => {
    const formValues = formRef?.current?.values?.data

    const requestToSend: any = {
      name: formValues?.folderName ?? '',
      parentFolderId: parentFolderId
    }

    if (isFolderStructure) {
      requestToSend.folderStructureTemplateTypeId = request?.folderStructureTemplateType?.id
    } else {
      requestToSend.folderTypeTemplateId = request?.folderTypeTemplateId?.id

      if (
        request?.folderTypeTemplateId?.documentTypesAllowed ===
          Constants?.system?.settings?.folderType?.documentTypesAllowed?.DEFINED_ON_FOLDER &&
        _.isArray(selectedDocTypes)
      ) {
        requestToSend.allowedDocumentTypeTemplateIds = selectedDocTypes.map((item: any) => item?.value || item?.id)
      }
    }

    createFolder(requestToSend).then(
      (data: any) => {
        window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'ADD_FOLDER' }))
        toast.success(translate('BROWSER_ADD_FOLDER_SUCCESS_MSG'))
        doClose()
      },
      (data: any) => {
        toast.error(translate('BROWSER_ADD_FOLDER_FAIL_MSG', { error: data ?? '' }))
        doClose()
      }
    )
  }

  const doClose = () => {
    setIsOpen(false)
  }

  return (
    <>
      <Modal
        show={isOpen}
        handleClose={doClose}
        title={
          isFolderStructure
            ? translate('BROWSER_ADD_FOLDER_STRUCTURE_H_TITLE')
            : translate('BROWSER_ADD_FOLDER_H_TITLE')
        }
        className={`theme-sandman add-folder-wrapper ${modalLevelClasses?.modalClass || ''}`}
      >
        <Modal.Body>
          <Formik
            initialValues={defaultConfigData}
            innerRef={formRef}
            validate={(values: any) => {
              const errors: any = { data: {} }
              if (values?.data?.folderName === '') {
                errors.data.folderName = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              if (!isValid(values?.data?.folderName ?? '', false)) {
                errors.data.folderName = 'FILENAME_CONTAINS_INVALID_CHARACTERS'
              }
              if (!values?.data?.folderType && !selectedFolderType && !isFolderStructure) {
                errors.data.folderType = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
              }
              // Remove availableFolderStructureTemplateTypes validation (not in values.data)
              return errors
            }}
            onSubmit={() => {}}
          >
            {({ setFieldValue, errors, touched }: any) => (
              <>
                <div className="flex-container bottom-spacer">
                  <Form.Label>{isFolderStructure ? translate('NAME') : translate('FOLDER_NAME')}</Form.Label>
                  <Field autoFocus type="text" name="data.folderName" maxLength={255} />
                  {isSubmitted && errors?.data?.folderName && (touched?.data?.folderName || isSubmitted) && (
                    <div className="error">{translate(errors.data.folderName)}</div>
                  )}
                </div>
                {!isFolderStructure && (
                  <div className="flex-container bottom-spacer">
                    <Form.Label>{translate('FOLDER_TYPE')}</Form.Label>
                    <Select
                      value={selectedFolderType ? selectedFolderType.id : null}
                      placeholder={translate('SELECT_FOLDER_TYPE_PLACEHOLDER')}
                      name="data.folderType"
                      options={folderTypes.length ? Utils.transformToSelectOptions(folderTypes, 'id', 'name') : []}
                      showNewOptionAtTop={true}
                      isClearable={false}
                      onChange={(option: any) => {
                        let selectedFolderTypeData = option
                        if (option && typeof option === 'object' && 'value' in option) {
                          const value = (option as any).value
                          selectedFolderTypeData = _.find(folderTypes, { id: value })
                          setRequest((requestData: any) => ({
                            ...requestData,
                            folderTypeTemplateId: selectedFolderTypeData
                          }))
                        }
                        setSelectedFolderType(selectedFolderTypeData)
                        setFieldValue('data.folderType', option)
                      }}
                    />
                    {errors?.data?.folderType && (touched?.data?.folderType || isSubmitted) && (
                      <div className="error">{translate(errors.data.folderType)}</div>
                    )}
                  </div>
                )}

                {isFolderStructure && (
                  <div className="bottom-spacer w50p">
                    <Form.Label>{translate('TYPE')}</Form.Label>
                    <Select
                      value={selectedFolderType ? selectedFolderType.id : null}
                      placeholder={translate('SELECT_FOLDER_TYPE_PLACEHOLDER')}
                      name="data.folderType"
                      options={
                        availableFolderStructureTemplateTypes.length
                          ? availableFolderStructureTemplateTypes.map((item) => ({ value: item.id, label: item.name }))
                          : []
                      }
                      showNewOptionAtTop={true}
                      onChange={(option: any) => {
                        let folderStructureTemplateTypeData = option
                        if (option && typeof option === 'object' && 'value' in option) {
                          const value = (option as any).value
                          folderStructureTemplateTypeData = _.find(availableFolderStructureTemplateTypes, {
                            id: value
                          })
                        }
                        setSelectedFolderType(folderStructureTemplateTypeData)
                        setRequest((requestData: any) => ({
                          ...requestData,
                          folderStructureTemplateType: folderStructureTemplateTypeData
                        }))
                        setFieldValue('data.folderStructureType', option)
                      }}
                    />
                    {errors?.data?.folderStructureType && (touched?.data?.folderStructureType || isSubmitted) && (
                      <div className="error">{translate(errors.data.folderStructureType)}</div>
                    )}
                  </div>
                )}

                {request?.folderTypeTemplateId?.documentTypesAllowed ===
                  Constants.system.settings.folderType.documentTypesAllowed.DEFINED_ON_FOLDER && (
                  <div className="form-group bottom-spacer">
                    <label htmlFor="eb66756d-66e9-41d5-a16d-6d09879285e7">{translate('DOC_TYPE_TITLE')}</label>
                    <DocTypeDropdown
                      name="crdoctype"
                      isSingle={false}
                      required={true}
                      id={'eb66756d-66e9-41d5-a16d-6d09879285e7'}
                      allowedTypes={allowedDocTypeIds}
                      value={selectedDocTypes}
                      onChange={(option: any) => {
                        setSelectedDocTypes(option || [])
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </Formik>
        </Modal.Body>
        <Modal.Footer>
          <div className="text-right">
            <Button className="btn btn-chita btn-close" variant="link" data-action={'cancel'} onClick={doClose}>
              {translate('CANCEL')}
            </Button>
            <Button
              data-action={'confirm'}
              className="btn btn-primary text-capitalize"
              onClick={() => {
                setIsSubmitted(true)
                formRef?.current?.validateForm().then(() => {
                  const errors = formRef.current.errors
                  // Check if errors.data has any keys (actual validation errors)
                  if (!errors.data || !Object.keys(errors.data).length) {
                    save()
                  }
                })
              }}
            >
              {isFolderStructure ? translate('BROWSER_ADD_FOLDER_STRUCTURE_BTN') : translate('SAVE')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default AddFolderModal
